import type { Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { generateUuid } from '@core/utils-legacy';
import { getImageDimensionsFormUrl, imageLoader } from '@core/wysiwyg-legacy';

import { updateSrc } from './image-commands';
import type { ImageNodeAttrs } from './image-node';
import type { ImageMenuData, ImageMenuResult } from './image-plugin.types';

export class ImagePluginService {
  public resetSize(pos: number, editorView: EditorView): Observable<void> {
    const imageNode = editorView.state.doc.nodeAt(pos);
    if (!imageNode?.attrs.src) return of();
    const { src } = <ImageNodeAttrs>imageNode.attrs;
    return getImageDimensionsFormUrl(src).pipe(
      tap(({ width }) => {
        const tr = editorView.state.tr.setNodeAttribute(pos, 'width', width);
        editorView.dispatch(tr);
      }),
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      map(() => {}),
    );
  }

  public deleteByGuid(guid: string, editorView: EditorView) {
    const tr = editorView.state.tr;
    let deleteFrom: number | undefined = undefined;
    let deleteTo = 0;
    tr.doc.descendants((node, pos) => {
      if (node.attrs.guid === guid) {
        deleteFrom = pos;
        deleteTo = deleteFrom + node.nodeSize;
      }
    });
    if (deleteFrom === undefined) {
      return;
    }
    tr.delete(deleteFrom, deleteTo);
    editorView.dispatch(tr);
  }

  public delete(pos: number, editorView: EditorView) {
    const tr = editorView.state.tr;
    const node = tr.doc.nodeAt(pos);
    if (!node) return;
    tr.delete(pos, pos + node.nodeSize);
    editorView.dispatch(tr);
  }

  public updateSrc({ id, url }: { id: string; url: string }, editorView: EditorView) {
    imageLoader(url).subscribe(() => {
      const cmd = updateSrc({ id, url });
      cmd(editorView.state, editorView.dispatch);
    });
  }

  public updateWidth(pos: number, width: number, editorView: EditorView) {
    const tr = editorView.state.tr.setNodeAttribute(pos, 'width', width);
    editorView.dispatch(tr);
  }

  public updateAttrs(
    pos: number,
    initialAttrs: ImageMenuData,
    updatedAttrs: ImageMenuResult,
    editorView: EditorView,
  ) {
    let tr = editorView.state.tr;
    let guid = '';

    const changedAttrs: Partial<ImageMenuData> = Object.keys(initialAttrs).reduce(
      (result, key: keyof ImageMenuData) => {
        if (updatedAttrs[key] !== initialAttrs[key]) {
          return {
            ...result,
            [key]: updatedAttrs[key],
          };
        }
        return result;
      },
      {},
    );

    if (changedAttrs.src && updatedAttrs.file) {
      const updateGuid = this.updateGuid(tr, pos);
      guid = updateGuid.guid;
      tr = updateGuid.tr;
    }

    Object.entries(changedAttrs).forEach(([attr, value]) => {
      tr.setNodeAttribute(pos, attr, value);
    });

    editorView.dispatch(tr);
    if (guid && updatedAttrs.file) {
      return { guid, file: updatedAttrs.file };
    }
    return null;
  }

  private updateGuid(tr: Transaction, pos: number): { tr: Transaction; guid: string } {
    const imageNode = tr.doc.nodeAt(pos);
    if (!imageNode) throw new Error(`Node missing at pos ${pos}`);
    const { guid } = imageNode.attrs;
    if (guid)
      return {
        tr,
        guid,
      };
    const newGuid = generateUuid();
    tr.setNodeAttribute(pos, 'guid', newGuid);
    return { tr, guid: newGuid };
  }
}
